/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ColDef,
  GridOptions,
  ICellRendererParams,
} from '@ag-grid-community/core'
import { Component, Input, OnDestroy } from '@angular/core'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  heliosDefaultColDef,
} from '@helios/ag-grid-config'
import { DensityLevel, UserPreferencesService } from '@helios/user-preferences'
import { fromUtc } from '@helios/utils'
import { GridComponents } from '@northfork/ag-grid'
import {
  DisplayDateFormat,
  DisplayDateTimeFormat24,
  formatDate,
} from '@northfork/basics'
import formatDistance from 'date-fns/formatDistance'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CorporateAction } from '../models/corporate-action'
import {
  corporateActionDetailFieldMap,
  CorporateActionDetailPropertyField,
  CorporateActionDetailsModel,
  corporateActionDetailsModelField,
  CorporateActionDetailValueField,
} from '../models/corporate-actions-details'
import { CorporateActionsService } from '../services/corporate-actions.service'
import { handleMandatoryKey, statusColor } from '../util/corporate-actions.util'
import { formatDates } from '@helios/common-api'

const JIRA_LINK = 'https://cwan.atlassian.net/browse/'
@Component({
  selector: 'hs-corporate-actions-details',
  templateUrl: './corporate-actions-details.component.html',
})
export class CorporateActionsDetailsComponent implements OnDestroy {
  _currentCorporateActionId: string | null = ''
  _accountId: number[] = []
  _issueId: string | undefined
  corporateAction: CorporateAction | undefined

  corporateActionOptions: GridOptions = this.createCorporateActionGridOptions()
  corporateActionRowData: CorporateActionDetailsModel[] = []

  issueAccountMap: Map<string, string> | undefined
  corporateActionDetailsLoading = false

  private readonly destroySubject$ = new Subject<void>()

  constructor(
    public corporateActionsService: CorporateActionsService,
    private readonly preferences: UserPreferencesService,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  createCorporateActionGridOptions(): GridOptions {
    return {
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        minWidth: 200,
        sortable: true,
      },
      columnDefs: this.corporateActionColDefs(),
      sideBar: false,
      onGridReady: params => {
        this.preferences
          .getDensityLevel()
          .pipe(takeUntil(this.destroySubject$))
          .subscribe(level =>
            enableHeliosGridDensityLevel(
              params.api,
              DensityLevel.Compact === level,
            ),
          )
        enableHeliosCopyBehavior(params.api)
      },
    }
  }

  corporateActionColDefs(): ColDef[] {
    return [
      {
        field: corporateActionDetailsModelField('property'),
        cellRenderer: (params: ICellRendererParams) => {
          if (
            params.data.property ===
            CorporateActionDetailPropertyField.NEEDSWORK
          ) {
            return ''
          }
          return params.data.property
        },
        cellClass: params => {
          if (
            params.data.property ===
            CorporateActionDetailPropertyField.NEEDSWORK
          ) {
            if (params.data.value) {
              return 'mdi mdi-alert text-danger'
            }
            return 'mdi mdi-alert'
          }
          return ''
        },
      },
      {
        field: corporateActionDetailsModelField('value'),
        cellRendererSelector: (params: ICellRendererParams) => {
          if (
            params.data.property === CorporateActionDetailPropertyField.JIRA
          ) {
            return {
              component: GridComponents.linkRenderer,
              params: {
                url: JIRA_LINK + params.data.value,
                newWindow: true,
              },
            }
          }
          return params.data.value
        },
        cellClass: params => {
          return this.getCellClass(params)
        },
        cellStyle: params => {
          if (
            params.data.property === CorporateActionDetailPropertyField.STATUS
          ) {
            return statusColor(params.data.value.toLowerCase())
          }
          return undefined
        },
      },
    ]
  }

  getCellClass(params: any) {
    if (params.data.property === CorporateActionDetailPropertyField.ACTION) {
      if (params.data.value === 'mandatory') {
        return 'text-danger'
      } else if (params.data.value && params.data.value != 'mandatory') {
        return 'text-warning'
      }
    } else if (
      params.data.property === CorporateActionDetailPropertyField.STATUS &&
      this.corporateAction?.reflagDateTime
    ) {
      return 'mdi mdi-flag'
    }
    return ''
  }

  @Input() set currentCorporateActionId(value: string | null) {
    if (this._currentCorporateActionId != value) {
      this._currentCorporateActionId = value

      this.getCorporateAction()
    }
  }

  @Input() set accountId(value: number[]) {
    if (this._accountId != value) {
      this._accountId = value
    }
  }

  @Input() set issueId(value: string | undefined) {
    this._issueId = value
  }

  getCorporateAction() {
    if (this._currentCorporateActionId) {
      this.corporateActionDetailsLoading = true
      this.corporateActionsService
        .searchById(
          this._currentCorporateActionId,
          this.accountId,
          this._issueId,
        )
        .subscribe(corporateAction => {
          this.corporateActionRowData = []
          this.corporateAction = corporateAction

          const corporateActionMap = new Map(Object.entries(corporateAction))

          corporateActionDetailFieldMap.forEach((value, key) => {
            this.handleKeys(corporateActionMap, key, value)
          })
          this.corporateActionDetailsLoading = false
        })
    }
  }

  handleKeys(corporateActionMap: Map<string, any>, key: string, value: string) {
    let valueData = corporateActionMap.get(key)
    if (key == CorporateActionDetailValueField.MANDATORY) {
      valueData = handleMandatoryKey(valueData)
      this.corporateActionRowData.push({
        property: value,
        value: valueData,
      })
    } else if (key == CorporateActionDetailValueField.STATUS) {
      valueData = this.handleStatusKey(valueData, corporateActionMap)
      this.corporateActionRowData.push({
        property: value,
        value: valueData,
      })
    } else if (key == CorporateActionDetailValueField.NEEDSWORK) {
      valueData = this.handleNeedsWorkKey(valueData, corporateActionMap)
      this.corporateActionRowData.push({
        property: value,
        value: valueData,
      })
    } else if (corporateActionMap.get(key)) {
      if (key == CorporateActionDetailValueField.TYPE) {
        valueData = corporateActionMap.get(key).label
        if (corporateActionMap.get(key).forRemoval) {
          valueData = corporateActionMap.get(key).label + '(DEPRECATED)'
        }
      }
      if (key == CorporateActionDetailValueField.EFFECTIVEDATE) {
        value = corporateActionMap.get(
          CorporateActionDetailValueField.TYPE,
        ).dateLabel
        valueData = formatDates(valueData, 'yyyy-MM-dd', 'MM/dd/yy')
      }
      if (
        key == CorporateActionDetailValueField.CREATEDDATETIME ||
        key == CorporateActionDetailValueField.UPDATEDDATETIME
      ) {
        valueData = formatDate(new Date(valueData), DisplayDateTimeFormat24)
      }

      this.corporateActionRowData.push({
        property: value,
        value: valueData,
      })
    }
  }

  handleNeedsWorkKey(valueData: any, corporateActionMap: Map<string, any>) {
    valueData = ''
    if (corporateActionMap.get(CorporateActionDetailValueField.NEEDSWORK)) {
      valueData = corporateActionMap.get(
        CorporateActionDetailValueField.NEEDSWORK,
      )
    }
    return valueData
  }

  handleStatusKey(valueData: any, corporateActionMap: Map<string, any>) {
    valueData = 'Unknown'
    if (
      corporateActionMap.get(CorporateActionDetailValueField.STATUS) &&
      corporateActionMap.get(CorporateActionDetailValueField.STATUS).name
    ) {
      valueData = corporateActionMap.get(
        CorporateActionDetailValueField.STATUS,
      ).name
    }
    if (this.corporateAction?.reflagDateTime) {
      const utc = fromUtc(this.corporateAction?.reflagDateTime)
      const diff = formatDistance(utc, new Date(), { addSuffix: true })
      valueData = valueData + '(' + diff + ')'
    }

    return valueData
  }
}
